import Cookies from 'js-cookie';
import flow from 'lodash/flow';
import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getSportsListByCountry, getSportsListByCurrency, noNeedCheckMatchCount } from '../helpers/sportsHelper';
import { useZone } from './useZone';
import type { ISportInfo } from '@/interface/ISportInfo';
import { ProductName, RoutePage, SportType } from '@/interface/enum';
import { moveElement } from '@/libraries/array';
import { useApp } from '@/libraries/composables/useApp';
import { isOlympicsZone } from '@/libraries/helpers/multipleSportsZoneHelper';
import { platform } from '@/libraries/helpers/platformHelper';
import { getArthasUrl, getHost } from '@/libraries/url';
import { useAccountStore } from '@/stores/accountStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

const eSportsImProductType = 9;

const virtualSportsTypeMapping: Partial<Record<SportType, number>> = {
    [SportType.VirtualFootball]: 6,
    [SportType.VirtualBasketball]: 3,
    [SportType.VirtualNBA]: 3,
};

export function useSportsList() {
    const { isMixParlayMode, sportType, isAsi } = storeToRefs(useRootStore());

    const hasEvents = (info: ISportInfo): boolean => {
        if (noNeedCheckMatchCount(info.sportType)) return true;
        const hasEvents = (isMixParlayMode.value ? info.hasMixParlayEvents : info.hasEvents);
        return hasEvents;
    };

    const hasLiveEvents = (info: ISportInfo): boolean => {
        if (noNeedCheckMatchCount(info.sportType)) return false;
        return isMixParlayMode.value ? info.hasLiveMixParlayEvents : info.hasLiveEvents;
    };

    const switchProduct = (productName: ProductName, sportType: SportType) => {
        platform.switchProduct({ productName, sportType });
    };

    const switchToVirtualSports = (sportType: SportType) => {
        if (virtualSportsTypeMapping[sportType]) {
            switchProduct(ProductName.VirtualSports, virtualSportsTypeMapping[sportType]);
        }
    };

    const handleSelectSportsInVirtualSports = (info: ISportInfo) => {
        if ([SportType.VirtualFootball, SportType.VirtualBasketball].includes(info.sportType)) { // sport switch to luna
            switchToVirtualSports(info.sportType);
        } else if (info.sportType === SportType.ESportsIm) { // sport switch to arthas
            switchProduct(ProductName.eSports, eSportsImProductType);
        } else if (info.sportType === SportType.Exchange) {
            if (isAsi.value) {
                switchProductToExchange();
            } else {
                platform.showLogin('Sports');
            }
        } else { // product switch to castor
            switchProduct(ProductName.Sports, info.sportType);
        }
    };

    function switchProductToExchange() {
        const threeMinutes = new Date(new Date().getTime() + 3 * 60 * 1000);
        Cookies.set('toSportsRoute', JSON.stringify({ name: RoutePage.Exchange }), {
            domain: getHost(),
            expires: threeMinutes,
        });
        switchProduct(ProductName.Sports, SportType.Cricket);
    }

    const { isInLeagueZone } = useZone();

    const route = useRoute();
    const router = useRouter();
    const handleSelectSportsInCasualSite = (info: ISportInfo) => {
        if (info.sportType === SportType.VirtualFootball || info.sportType === SportType.VirtualBasketball) {
            switchToVirtualSports(info.sportType);
        } else if (info.sportType === SportType.ESportsIm) { // sport switch to arthas
            switchProduct(ProductName.eSports, eSportsImProductType);
        } else if (info.sportType === SportType.Exchange) {
            if (isAsi.value) {
                router.push({ name: RoutePage.Exchange });
            } else {
                platform.showLogin('Sports');
            }
        } else {
            if (!hasEvents(info)) return;

            const isSameRoute = route.name === RoutePage.MainBySport && sportType.value === info.sportType;
            if (!isSameRoute || isInLeagueZone.value) {
                const sportRouteName = SportType.toRouteName(info.sportType);
                router.push({ name: RoutePage.MainBySport, params: { sportRouteName } });
            }
        }
    };
    const switchSport = (info: ISportInfo, isInOtherProduct = false) => {
        if (isInOtherProduct) {
            handleSelectSportsInVirtualSports(info);
        } else {
            handleSelectSportsInCasualSite(info);
        }
    };

    const {
        sportsWithNewTagConfig,
        isMultipleSportsZoneEnabled,
        isFairExchangeEnabled,
        defaultSportType,
        esportsNotAllowCurrencies,
        isMuayThaiOneBxInSportEnabled,
        muayThaiOneBxAllowedPlayerTypes,
    } = storeToRefs(useFeatureToggleStore());
    const { isIomB2c, customerInfo } = storeToRefs(useAccountStore());

    const isOneBxAvailable = computed(() => isAsi.value
        && isMuayThaiOneBxInSportEnabled.value
        && muayThaiOneBxAllowedPlayerTypes.value.includes(customerInfo.value.playerType));

    function redirectToMuayThaiOneBx() {
        window.location.href = `${getArthasUrl()}/SportsPlus/Open?Device=Mobile&Provider=ONEBX&isInSports=true`;
    }

    function isShowNewTag(sportType: SportType) {
        return sportsWithNewTagConfig.value.includes(sportType);
    }

    const removeOlympicPipe = (results: ISportInfo[]) => {
        if (isMultipleSportsZoneEnabled.value && isOlympicsZone()) {
            return results.filter(info => info.sportType !== SportType.Olympic);
        }
        return [...results];
    };

    const { isFromApp } = useApp();

    const addESportsPipe = (results: ISportInfo[]) => {
        const cloneResults = [...results];
        const esportsIndex = cloneResults.findIndex(info => info.sportType === SportType.ESports);
        const isNotAllowCurrency = esportsNotAllowCurrencies.value.includes(customerInfo.value.currency);
        const shouldShowEsportsIm = isAsi.value
            && !isIomB2c.value
            && esportsIndex !== -1
            && !isFromApp
            && !isNotAllowCurrency;

        if (!shouldShowEsportsIm) {
            return cloneResults;
        }

        const sboEsports = cloneResults[esportsIndex];
        const esportsChildren = [
            createSportInfo({ sportType: SportType.ESportsIm, hasLiveMixParlayEvents: false }),
            createSportInfo(sboEsports),
        ];
        const eSportsInfo: ISportInfo = createSportInfo({
            sportType: SportType.ESportsForDisplay,
            hasLiveMixParlayEvents: false,
            infos: esportsChildren,
        });

        cloneResults.splice(esportsIndex, 1, eSportsInfo);

        return cloneResults;
    };

    const addMuayThaiPipe = (results: ISportInfo[]) => {
        const cloneResults = [...results];
        if (!isOneBxAvailable.value) return cloneResults;
        const muayThaiIndex = cloneResults.findIndex(info => info.sportType === SportType.MuayThai);
        if (muayThaiIndex === -1) return cloneResults;
        const sboMuayThai = cloneResults[muayThaiIndex];
        const muayThaiInfo: ISportInfo = createSportInfo({
            sportType: SportType.MuayThaiForDisplay,
            hasLiveMixParlayEvents: false,
            infos: [
                createSportInfo(sboMuayThai),
                createSportInfo({ sportType: SportType.MuayThaiOneBx, hasLiveMixParlayEvents: false, isNew: true }),
            ],
        });
        cloneResults.splice(muayThaiIndex, 1, muayThaiInfo);
        return cloneResults;
    };

    function addExchangePipe(results: ISportInfo[]) {
        const cloneResults = [...results];

        if (!isFairExchangeEnabled.value) {
            return cloneResults;
        }

        const exchangeIndex = cloneResults.findIndex(x => x.sportType === SportType.Exchange);
        if (exchangeIndex !== -1) {
            return cloneResults;
        }

        const cricketIndex = cloneResults.findIndex(x => x.sportType === SportType.Cricket);
        const exchange = createSportInfo({ sportType: SportType.Exchange, hasLiveMixParlayEvents: false });
        cloneResults.splice(cricketIndex + 1, 0, exchange);

        return cloneResults;
    }

    function createSportInfo(sportInfo: Partial<ISportInfo>): ISportInfo {
        return {
            sportType: SportType.Unknown,
            hasEvents: true,
            hasLiveEvents: true,
            hasMixParlayEvents: true,
            hasLiveMixParlayEvents: true,
            isNew: isShowNewTag(sportInfo.sportType!),
            infos: null,
            ...sportInfo,
        };
    }

    const addVirtualSportsPipe = (results: ISportInfo[]) => {
        const cloneResults = [...results];
        if (!isAsi.value || isFromApp) {
            return cloneResults;
        }
        const footballIndex = cloneResults.findIndex(x => x.sportType === SportType.Soccer);
        const virtualFootball = createSportInfo({
            sportType: SportType.VirtualFootball,
            hasLiveMixParlayEvents: false,
        });
        cloneResults.splice(footballIndex + 1, 0, virtualFootball);

        const basketballIndex = cloneResults.findIndex(x => x.sportType === SportType.Basketball);
        const virtualBasketball = createSportInfo({
            sportType: SportType.VirtualBasketball,
            hasLiveMixParlayEvents: false,
        });
        cloneResults.splice(basketballIndex + 1, 0, virtualBasketball);
        return cloneResults;
    };

    const sortByDefaultOrderPipe = (results: ISportInfo[]) => {
        const defaultSportsOrder = isAsi.value
            ? getSportsListByCurrency(customerInfo.value.currency)
            : getSportsListByCountry(customerInfo.value.ipCountryCode);

        return orderBy(results, [
            info => defaultSportsOrder.indexOf(info.sportType),
        ], [
            'asc',
        ]);
    };

    const sortByDefaultSportConfigPipe = (results: ISportInfo[]) => {
        const cloneResults = [...results];

        const sportInfo = cloneResults.find(info => info.sportType === defaultSportType.value);

        if (!sportInfo) {
            return cloneResults;
        }

        moveSportsToTop(defaultSportType.value, cloneResults);
        return cloneResults;
    };

    function moveSportsToTop(needMoveSportType: SportType, list: ISportInfo[]) {
        const from = list.findIndex(item => item.sportType === needMoveSportType);
        const to = 0;

        moveElement(list, from, to);
    }

    const sortByLiveThenNonLivePipe = (results: ISportInfo[]) => {
        if (results.length === 0) return results;
        results = orderBy(results, [
            info => (isMixParlayMode.value ? info.hasLiveMixParlayEvents : info.hasLiveEvents),
            info => (isMixParlayMode.value ? info.hasMixParlayEvents : info.hasEvents),
        ], [
            'desc',
            'desc',
        ]);
        return results;
    };

    const sortSportsList = (results: ISportInfo[]): ISportInfo[] => {
        const sortSportsListPipe = flow(
            removeOlympicPipe,
            sortByDefaultOrderPipe,
            sortByDefaultSportConfigPipe,
            addESportsPipe,
            addMuayThaiPipe,
            sortByLiveThenNonLivePipe,
            addVirtualSportsPipe,
            addExchangePipe,
        );
        return sortSportsListPipe(results);
    };

    return {
        hasEvents,
        hasLiveEvents,
        switchSport,
        switchToVirtualSports,
        sortSportsList,
        isOneBxAvailable,
        redirectToMuayThaiOneBx,
    };
}
